import { Box, Paper, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";

export type PageCommandBarProps = {
  /** Optional leading control (back button). */
  leading?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-side primary/secondary actions (max ~3 visible). */
  actions?: ReactNode;
  /** Sticky under the app navbar for edit/studio screens. */
  sticky?: boolean;
};

/**
 * Win11-style command bar shared by edit/studio screens.
 * Same mental model as PageHeader on list pages: title left, actions right.
 */
export default function PageCommandBar({
  leading,
  title,
  subtitle,
  actions,
  sticky = true,
}: PageCommandBarProps) {
  return (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 1.25, sm: 1.75 },
        py: 1,
        mb: 0,
        borderRadius: 1.25,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 1px 2px rgba(0,0,0,0.35)"
            : "0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.04)",
        ...(sticky
          ? {
              position: "sticky",
              top: 0,
              zIndex: 8,
            }
          : {}),
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center" minWidth={0} flex={1}>
          {leading}
          <Box minWidth={0}>
            <Typography variant="h6" component="h1" fontWeight={650} noWrap sx={{ lineHeight: 1.25 }}>
              {title}
            </Typography>
            {subtitle != null && subtitle !== false && (
              <Box mt={0.15}>
                {typeof subtitle === "string" || typeof subtitle === "number" ? (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {subtitle}
                  </Typography>
                ) : (
                  subtitle
                )}
              </Box>
            )}
          </Box>
        </Stack>
        {actions != null && (
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
            alignItems="center"
            flexShrink={0}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
